import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  LinearProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Tooltip,
} from "@mui/material";
import {
  FileSpreadsheet,
  FileCheck,
  CheckCircle,
  AlertCircle,
  Info,
  Download,
} from "lucide-react";
import {
  ITransactionUploadProgress,
  ITransactionUploadSummary,
} from "../../types/models";
import { DuplicateActionEnumModel } from "../../types/types";
import { exportFailedPropMixLookups } from "../../store/modules/admin/propMixActions";
import { exportFailedImports } from "../../store/modules/transactions/transactionActions";

interface ImportDialogProps {
  open: boolean;
  onClose: () => void;
  selectedFile: File | null;
  isUploading: boolean;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleUpload: () => void;
  uploadProgress?: ITransactionUploadProgress | null;
  uploadSummary?: ITransactionUploadSummary | null;
  connectionError?: string | null;
  duplicateHandling: DuplicateActionEnumModel | undefined;
  onDuplicateHandlingChange: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  isAdmin?: boolean;
  enablePropMixLookup?: boolean;
  onPropMixLookupChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  "aria-labelledby"?: string;
}

const ImportExcelDialog: React.FC<ImportDialogProps> = (props) => {
  const {
    open,
    onClose,
    selectedFile,
    isUploading,
    handleFileChange,
    handleUpload,
    uploadProgress,
    uploadSummary,
    connectionError,
    duplicateHandling,
    onDuplicateHandlingChange,
    isAdmin,
    enablePropMixLookup,
    onPropMixLookupChange,
  } = props;
  const [completionTime, setCompletionTime] = useState<string>("");

  useEffect(() => {
    if (uploadSummary && uploadSummary.isComplete && !isUploading) {
      setCompletionTime(new Date().toLocaleTimeString());
    }
  }, [uploadSummary, isUploading]);
  return (
    <Dialog
      open={open}
      // onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby={props["aria-labelledby"] || "import-dialog-title"}
    >
      <DialogTitle id="import-dialog-title">
        {uploadSummary && uploadSummary.isComplete && !isUploading
          ? "Import Summary"
          : "Import Transactions"}
      </DialogTitle>
      <DialogContent>
        {/* File Selection Section - Hide when summary is displayed */}
        {!(uploadSummary && uploadSummary.isComplete && !isUploading) && (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Please select an Excel file containing your transaction data
              </Typography>
            </Box>

            <Box
              sx={{
                border: "2px dashed",
                borderColor: "divider",
                borderRadius: 1,
                p: 3,
                textAlign: "center",
                mb: 2,
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  borderColor: "primary.main",
                  backgroundColor: "rgba(0, 0, 0, 0.01)",
                },
              }}
            >
              <label
                htmlFor="file-upload"
                aria-label="Select Excel file to upload"
              >
                <input
                  type="file"
                  id="file-upload"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                >
                  <FileSpreadsheet
                    size={48}
                    style={{ marginBottom: 8, color: "#6c757d" }}
                  />
                  <Typography variant="body2" fontWeight="medium">
                    {selectedFile ? selectedFile.name : "Click to select file"}
                  </Typography>
                  {selectedFile && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </Typography>
                  )}
                </Box>
              </label>
            </Box>

            {selectedFile && !isUploading && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "success.main",
                  mb: 2,
                }}
              >
                <FileCheck size={16} style={{ marginRight: 8 }} />
                <Typography variant="body2">File ready to upload</Typography>
              </Box>
            )}

            {selectedFile && !isUploading && (
              <Box sx={{ mt: 3, mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  How should duplicate transactions be handled?
                </Typography>
                <RadioGroup
                  value={duplicateHandling}
                  onChange={onDuplicateHandlingChange}
                  aria-label="Duplicate handling options"
                >
                  <FormControlLabel
                    value={DuplicateActionEnumModel.Skip}
                    control={<Radio size="small" />}
                    label={
                      <Typography variant="body2">
                        Ignore - Skip transactions that already exist
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    value={DuplicateActionEnumModel.Overwrite}
                    control={<Radio size="small" />}
                    label={
                      <Typography variant="body2">
                        {/* Overwrite - Replace existing records with imported data */}
                        Replace - Delete duplicate transactions and replace them
                        with new uploads
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    value={DuplicateActionEnumModel.AddAsNew}
                    control={<Radio size="small" />}
                    label={
                      <Typography variant="body2">
                        {/* Add as New - Import all as new records */}
                        Duplicate - Add as new, potentially creating duplicate
                        transactions
                      </Typography>
                    }
                  />
                </RadioGroup>
              </Box>
            )}

            {/* PropMix Lookup Option - Admin Only */}
            {selectedFile && !isUploading && isAdmin && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <Divider sx={{ mb: 2 }} />
                <Tooltip
                  title="Use PropMix API to automatically look up buyer/seller names based on property address. This uses shared API quota."
                  arrow
                  placement="top"
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={enablePropMixLookup}
                        onChange={onPropMixLookupChange}
                        size="small"
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="body2">
                        Enable PropMix client name lookup (Admin only)
                      </Typography>
                    }
                  />
                </Tooltip>
                {enablePropMixLookup && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    PropMix will attempt to auto-populate buyer/seller names for
                    each transaction. Failed lookups will be logged for manual
                    retry.
                  </Alert>
                )}
              </Box>
            )}
          </>
        )}

        {isUploading && uploadProgress && (
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Upload Progress
            </Typography>
            <LinearProgress
              variant="determinate"
              value={
                uploadProgress.processedCount > 0
                  ? (100 *
                      (uploadProgress.insertedCount +
                        uploadProgress.duplicateCount)) /
                    uploadProgress.processedCount
                  : 0
              }
              sx={{ mb: 1 }}
              aria-label="Upload progress"
              role="progressbar"
              aria-valuenow={
                uploadProgress.processedCount > 0
                  ? Math.round(
                      (100 *
                        (uploadProgress.insertedCount +
                          uploadProgress.duplicateCount)) /
                        uploadProgress.processedCount
                    )
                  : 0
              }
              aria-valuemin={0}
              aria-valuemax={100}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "0.75rem",
                color: "text.secondary",
              }}
            >
              <Typography variant="body2">
                Processed: {uploadProgress.processedCount}
              </Typography>
              <Typography variant="body2">
                Inserted: {uploadProgress.insertedCount}
              </Typography>
              <Typography variant="body2">
                Duplicates: {uploadProgress.duplicateCount}
              </Typography>
            </Box>
          </Box>
        )}

        {connectionError && (
          <Box sx={{ mt: 2, mb: 2, color: "error.main" }}>
            <Typography variant="body2" color="error">
              {connectionError}
            </Typography>
          </Box>
        )}

        {/* Upload Summary Section */}
        {uploadSummary && uploadSummary.isComplete && !isUploading && (
          <Box sx={{ mt: 1, mb: 1 }}>
            <Paper
              elevation={0}
              sx={{ p: 2, bgcolor: "background.default", mb: 2 }}
            >
              <Box sx={{ mb: 2 }}>
                {selectedFile && (
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <FileSpreadsheet
                      size={16}
                      style={{ marginRight: 8, color: "#6c757d" }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {selectedFile.name}
                    </Typography>
                  </Box>
                )}
                <Typography variant="caption" color="text.secondary">
                  Completed at {completionTime}
                </Typography>
              </Box>

              <Alert
                severity={
                  uploadSummary.errors.length > 0 ? "warning" : "success"
                }
                icon={
                  uploadSummary.errors.length > 0 ? (
                    <AlertCircle />
                  ) : (
                    <CheckCircle />
                  )
                }
                sx={{ mb: 2 }}
              >
                {uploadSummary.errors.length > 0
                  ? `Upload completed with ${uploadSummary.errors.length} errors`
                  : `Successfully processed ${uploadSummary.totalProcessed} transactions`}
              </Alert>

              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle size={18} color="#4caf50" />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${uploadSummary.totalInserted} records inserted`}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <Info size={18} color="#2196f3" />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${
                      duplicateHandling?.toString() ===
                      DuplicateActionEnumModel.Skip.toString()
                        ? uploadSummary.totalDuplicates
                        : uploadSummary.totalUpdated
                    } duplicate records ${
                      duplicateHandling?.toString() ===
                      DuplicateActionEnumModel.Skip.toString()
                        ? "skipped"
                        : "replaced"
                    }`}
                  />
                </ListItem>

                {uploadSummary.errors.length > 0 && (
                  <>
                    <ListItem>
                      <ListItemIcon>
                        <AlertCircle size={18} color="#f44336" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${uploadSummary.errors.length} errors encountered`}
                        secondary={
                          uploadSummary.errors.length === 1
                            ? uploadSummary.errors[0]
                            : `${uploadSummary.errors[0]}`
                        }
                      />
                    </ListItem>

                    {uploadSummary.errors.length > 1 && (
                      <Box sx={{ mt: 1, pl: 6 }}>
                        <Divider sx={{ my: 1 }} />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mb: 1 }}
                        >
                          Additional Errors:
                        </Typography>
                        <Box
                          sx={{ maxHeight: "120px", overflowY: "auto", pr: 1 }}
                        >
                          {uploadSummary.errors.slice(1).map((error, index) => (
                            <Typography
                              key={index}
                              variant="body2"
                              color="error"
                              sx={{
                                fontSize: "0.75rem",
                                mb: 0.5,
                                display: "flex",
                                alignItems: "flex-start",
                              }}
                            >
                              <Box component="span" sx={{ mr: 1, mt: 0.5 }}>
                                •
                              </Box>
                              {error}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </>
                )}

                {/* PropMix Stats - Only show if PropMix was enabled */}
                {uploadSummary.propMixEnabled && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <ListItem>
                      <ListItemIcon>
                        <Info size={18} color="#9c27b0" />
                      </ListItemIcon>
                      <ListItemText
                        primary="PropMix Client Name Lookup"
                        secondary={`✓ ${
                          uploadSummary.propMixLookupsSuccess || 0
                        } successful | ✗ ${
                          uploadSummary.propMixLookupsFailed || 0
                        } unsuccessful`}
                      />
                    </ListItem>

                    {/* Export Failed Lookups Button */}
                    {(uploadSummary.propMixLookupsFailed || 0) > 0 &&
                      uploadSummary.batchId && (
                        <ListItem>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Download size={16} />}
                            onClick={async () => {
                              try {
                                const blob = await exportFailedPropMixLookups(
                                  uploadSummary.batchId!
                                );
                                const url = window.URL.createObjectURL(blob);
                                const link = document.createElement("a");
                                link.href = url;
                                link.download = `Failed_PropMix_Lookups_${
                                  new Date().toISOString().split("T")[0]
                                }.xlsx`;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(url);
                              } catch (error) {
                                console.error("Failed to export:", error);
                                // You can add a toast/snackbar notification here
                              }
                            }}
                            fullWidth
                            sx={{ mt: 1 }}
                          >
                            Export Failed Lookups to Excel
                          </Button>
                        </ListItem>
                      )}
                  </>
                )}

                {/* Import Failures - Records that failed to import */}
                {(uploadSummary.validationFailures || 0) > 0 && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <ListItem>
                      <ListItemIcon>
                        <AlertCircle size={18} color="#f44336" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Import Failures"
                        secondary={`${
                          uploadSummary.validationFailures || 0
                        } record(s) failed to import due to missing required fields`}
                      />
                    </ListItem>
                  </>
                )}

                {/* Address Verification Issues - Records imported but address not verified */}
                {(uploadSummary.geocodingFailures || 0) > 0 && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <ListItem>
                      <ListItemIcon>
                        <AlertCircle size={18} color="#ff9800" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Address Verification"
                        secondary={`${
                          uploadSummary.geocodingFailures || 0
                        } record(s) imported successfully but address could not be verified by Google`}
                      />
                    </ListItem>
                  </>
                )}

                {/* Export Failed/Unverified Records Button */}
                {uploadSummary.batchId &&
                  ((uploadSummary.validationFailures || 0) > 0 ||
                    (uploadSummary.geocodingFailures || 0) > 0) && (
                    <ListItem>
                      <Button
                        variant="outlined"
                        size="small"
                        color="warning"
                        startIcon={<Download size={16} />}
                        onClick={async () => {
                          try {
                            const blob = await exportFailedImports(
                              uploadSummary.batchId!
                            );
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement("a");
                            link.href = url;
                            link.download = `Import_Issues_${
                              new Date().toISOString().split("T")[0]
                            }.xlsx`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(url);
                          } catch (error) {
                            console.error("Failed to export:", error);
                            // You can add a toast/snackbar notification here
                          }
                        }}
                        fullWidth
                        sx={{ mt: 1 }}
                      >
                        Export Import Issues to Excel
                      </Button>
                    </ListItem>
                  )}
              </List>
            </Paper>
          </Box>
        )}

        {/* Requirements Section - Updated with template info */}
        {!(uploadSummary && uploadSummary.isComplete && !isUploading) && (
          <Box sx={{ px: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Requirements:
            </Typography>
            <Box
              component="ul"
              sx={{
                marginLeft: 2,
                color: "text.secondary",
                fontSize: "0.75rem",
                backgroundColor: "grey.50",
                p: 1.5,
                borderRadius: 1,
                borderLeft: "4px solid",
                borderColor: "info.light",
              }}
            >
              <li>File must be .xlsx, .xls or .csv format</li>
              <li>First row must contain column headers</li>
              <li>Use the downloaded template format for best results</li>
              <li>Date format should be YYYY-MM-DD or MM/DD/YYYY</li>
              <li>
                No Status column needed: a Closing Date in the past or today is
                imported as a Closed Transaction, a future one as Under Contract
                (Pipeline folder)
              </li>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {uploadSummary && uploadSummary.isComplete && !isUploading ? (
          // Show only Close button when summary is displayed
          <Button
            variant="contained"
            onClick={onClose}
            color="primary"
            fullWidth={window.innerWidth < 600}
          >
            Close
          </Button>
        ) : (
          <>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{ mr: 1 }}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={
                !selectedFile || isUploading || duplicateHandling === undefined
              }
              startIcon={isUploading ? <CircularProgress size={16} /> : null}
              aria-busy={isUploading}
            >
              {isUploading ? "Processing..." : "Upload"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportExcelDialog;
